import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';

import { Maybe, MaybeNull } from 'types';
import { addQuery, parseQuery } from 'helpers/query';
import { isPublicWebsite } from 'helpers/html';
import { useSlug } from 'shared/hooks/useSlug';

export const useQueryState = <T = string>(
  name: string,
): [Maybe<T>, (value?: MaybeNull<T>) => void] => {
  const navigate = useNavigate();
  const location = useLocation();

  const value = useMemo(
    () => parseQuery(location.search)[name] as unknown,
    [location.search, name],
  );

  const setValue = useCallback(
    (value?: MaybeNull<T>) => {
      navigate({
        search: addQuery(location.search, { [name]: value }),
      });
    },
    [navigate, location.search, name],
  );

  const { slug: resolvedSlug } = useSlug();

  if (name === 'slug' && isPublicWebsite()) {
    const fromQuery = Array.isArray(value) ? value[0] : value;
    const slugFromQuery = typeof fromQuery === 'string' ? fromQuery : null;

    // @ts-ignore
    return [(slugFromQuery ?? resolvedSlug) as Maybe<T>, setValue];
  }

  return [Array.isArray(value) ? value[0] : value, setValue];
};